import { AnalyticsParams } from "../analyticsClient";
import { compactAnalyticsParams } from "./common";

export type RecsysRequestParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  streamKey: string;
  endpoint: string;
  page: number;
  sectionCount: number;
  totalItemCount: number;
};

export type RecsysSectionServeParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  sectionType: string;
  isAuto: boolean;
  sectionTitle: string;
  itemType: string;
  itemCount: number;
};

export type RecsysSectionListViewParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  lastVisiblePosition: number;
};

export type RecsysSectionLastViewParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  lastVisiblePosition: number;
};

export type RecsysSectionViewAllParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
};

export type RecsysItemServeParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  itemType: string;
  itemId: string;
  position: string;
  impressionId: string;
};

export type RecsysItemListViewParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  lastVisiblePosition: number;
};

export type RecsysItemLastViewParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  lastVisiblePosition: number;
};

export type RecsysItemClickParamsInput = {
  streamSessionId: string;
  requestUuid: string;
  impressionId: string;
  sectionId: string | number;
  sectionPosition: number;
  itemType: string;
  itemId: string | number;
  position: number;
  firstVisibleAtMs?: number;
  clickedAtMs?: number;
};

export function buildRecsysRequestParams(
  input: RecsysRequestParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    stream_key: input.streamKey,
    endpoint: input.endpoint,
    page: input.page,
    section_count: input.sectionCount,
    total_item_count: input.totalItemCount,
  });
}

export function buildRecsysSectionServeParams(
  input: RecsysSectionServeParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
    section_type: input.sectionType,
    is_auto: input.isAuto,
    section_title: input.sectionTitle,
    item_type: input.itemType,
    item_count: input.itemCount,
  });
}

export function buildRecsysSectionListViewParams(
  input: RecsysSectionListViewParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    last_visible_position: input.lastVisiblePosition,
  });
}

export function buildRecsysSectionLastViewParams(
  input: RecsysSectionLastViewParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    last_visible_position: input.lastVisiblePosition,
  });
}

export function buildRecsysSectionViewAllParams(
  input: RecsysSectionViewAllParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
  });
}

export function buildRecsysItemServeParams(
  input: RecsysItemServeParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    impression_id: input.impressionId,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
    item_type: input.itemType,
    item_id: input.itemId,
    position: input.position,
  });
}

export function buildRecsysItemListViewParams(
  input: RecsysItemListViewParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
    last_visible_position: input.lastVisiblePosition,
  });
}

export function buildRecsysItemLastViewParams(
  input: RecsysItemLastViewParamsInput,
): AnalyticsParams {
  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
    last_visible_position: input.lastVisiblePosition,
  });
}

export function buildRecsysItemClickParams(
  input: RecsysItemClickParamsInput,
): AnalyticsParams {
  const clickTime = input.clickedAtMs ?? Date.now();
  const timeToClickMs =
    typeof input.firstVisibleAtMs === "number"
      ? Math.max(0, clickTime - input.firstVisibleAtMs)
      : undefined;

  return compactAnalyticsParams({
    stream_session_id: input.streamSessionId,
    request_uuid: input.requestUuid,
    impression_id: input.impressionId,
    section_id: String(input.sectionId),
    section_position: input.sectionPosition,
    item_type: input.itemType,
    item_id: String(input.itemId),
    position: input.position,
    time_to_click_ms: timeToClickMs,
  });
}
